import { IS_BABYLON_NATIVE_JSCORE } from "../utils/utils";

export class VideoPoolConfig {
  public videoPrefix: string = "ejw-video";
  public oneAtATimeUnlock: boolean = true;
  public autoPlay: boolean = true;
  public loop: boolean = true;
  public muted: boolean = false;
  public hidden: boolean = true;
  public parentElement?: HTMLElement;
  public numOfVideos: number = 1;
}

export default class VideoPool {
  private config: VideoPoolConfig;
  private videos: HTMLVideoElement[] = [];
  private videosToUnlock: HTMLVideoElement[] = [];
  private pointerUnlockActive: boolean = false;
  private nextVideoIndex: number = 0;
  private muted: boolean = false;
  private unlockBound: () => void;

  constructor(config?: VideoPoolConfig) {
    this.config = config ?? new VideoPoolConfig();
    this.muted = this.config.muted;
    this.unlockBound = this.unlock.bind(this);
    this.init();
    this.bindPointerUnlock();
  }

  //---------------------------------------------------------- init / dispose.
  public dispose(): void {
    this.unbindPointerUnlock();
    this.videos.forEach(video => {
      video.pause();
      video.src = "";
      video.load();
      if (video.parentElement) {
        video.parentElement.removeChild(video);
      }
    });
    this.videos = [];
    this.videosToUnlock = [];
    this.nextVideoIndex = 0;
  }

  private init(): void {
    // Babylon Native (JSCore): no DOM, but the runtime provides a global
    // HTMLVideoElement (backed by a native AVPlayer). Create pool elements
    // directly; there is no autoplay gesture-gate on native, so they are
    // born unlocked (no pointer-unlock flow). The document guard keeps other
    // DOM-less environments (node/vitest, SSR) out of the DOM branch below.
    if (IS_BABYLON_NATIVE_JSCORE || typeof document === "undefined") {
      // NOTE: on JSC, napi-defined class constructors report typeof "object"
      // (not "function"), so only check for presence.
      const NativeVideo = (globalThis as any).HTMLVideoElement;
      if (!NativeVideo) {
        return;
      }
      for (let i = 0; i < this.config.numOfVideos; i++) {
        const video = new NativeVideo() as HTMLVideoElement;
        (video as any).id = `${this.config.videoPrefix}-${i + 1}`;
        video.autoplay = this.config.autoPlay;
        video.loop = this.config.loop;
        video.muted = this.config.muted;
        this.videos.push(video);
      }
      return;
    }
    const parentElement = this.config.parentElement ?? document.body;
    for (let i = 0; i < this.config.numOfVideos; i++) {
      const video = document.createElement("video");
      video.id = `${this.config.videoPrefix}-${i + 1}`;
      video.autoplay = this.config.autoPlay;
      video.loop = this.config.loop;
      video.muted = true; // locked by default
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      if (this.config.hidden) {
        video.style.display = "none";
      }
      parentElement.appendChild(video);
      this.videos.push(video);
      this.videosToUnlock.push(video);
    }
  }

  //---------------------------------------------------------- video getters.
  public getVideos(): HTMLVideoElement[] {
    return [...this.videos];
  }

  public getVideoById(id: string): HTMLVideoElement | undefined {
    return this.videos.find(video => video.id === id);
  }

  public getVideoByIndex(index: number): HTMLVideoElement | undefined {
    if (index < 0 || index >= this.videos.length) {
      return undefined;
    }
    return this.videos[index];
  }

  public getVideoByNextIndex(): HTMLVideoElement | undefined {
    if (this.videos.length === 0) {
      return undefined;
    }
    const video = this.videos[this.nextVideoIndex % this.videos.length];
    this.nextVideoIndex++;
    return video;
  }

  //---------------------------------------------------------- audio.
  public setMuted(muted: boolean): void {
    this.muted = muted;
    // apply mute state to unlocked videos only.
    this.videos.forEach(video => {
      const isPendingUnlock = this.videosToUnlock.includes(video);
      video.muted = isPendingUnlock ? true : this.muted;
    });
  }

  public getMuted(): boolean {
    return this.muted;
  }

  //---------------------------------------------------------- video unlock.
  private bindPointerUnlock(): void {
    if (typeof window === "undefined") {
      return;
    }
    if (this.pointerUnlockActive) {
      return;
    }
    window.addEventListener("pointerdown", this.unlockBound);
    this.pointerUnlockActive = true;
  }

  private unbindPointerUnlock(): void {
    if (typeof window === "undefined") {
      return;
    }
    if (!this.pointerUnlockActive) {
      return;
    }
    window.removeEventListener("pointerdown", this.unlockBound);
    this.pointerUnlockActive = false;
  }

  private unlock(): void {
    if (this.videosToUnlock.length === 0) {
      this.unbindPointerUnlock();
      return;
    }
    if (this.config.oneAtATimeUnlock) {
      const video = this.videosToUnlock[0];
      try {
        video.muted = this.muted;
        console.log(`Video unlocked - ${video.id}`);
        this.videosToUnlock.shift();
      } catch (error) {
        console.error(`Failed to unlock video - ${video.id}:`, error);
      }
    } else {
      const failedVideos: HTMLVideoElement[] = [];
      this.videosToUnlock.forEach(video => {
        try {
          video.muted = this.muted;
          console.log(`Video unlocked - ${video.id}`);
        } catch (error) {
          failedVideos.push(video);
          console.error(`Failed to unlock video - ${video.id}:`, error);
        }
      });
      this.videosToUnlock = failedVideos;
    }
    if (this.videosToUnlock.length === 0) {
      this.unbindPointerUnlock();
    }
  }
}
